<template>
  <div ref="banners">
    <v-alert
      v-for="banner in banners"
      :key="banner.id"
      class="mt-4"
      dismissible
      border="top"
      dark
      transition="fade-transition"
      :color="mapVariantToColor(banner.variant)"
      @input="bannerChanged($event, banner)">
      <span v-html="banner.content" @click="contentClicked($event, banner)"/>
    </v-alert>
  </div>
</template>

<script>
/**
 * Display all banners stored in vuex.
 */
export default {
  name: 'AppBanners',
  computed: {
    banners () {
      return this.$store.getters['globalMessagingModule/getAllBanners']
    },
    // Extra computed property to facilitate watching length of banners array. If we watch the array directly,
    // we don't see any changes because it's mutated and Vue watchers can't keep track of mutated changes.
    bannerLength () {
      return this.banners.length
    }
  },
  methods: {
    dismissBanner (banner) {
      this.$store.dispatch('globalMessagingModule/removeBanner', banner)
    },
    /**
     * We are sharing the vuex state for errors with Bootstrap, so need to map the original bootstrap variants to
     * Vuetify theme colors.
     * @param variant
     * @returns {string}
     */
    mapVariantToColor (variant) {
      switch (variant) {
        case 'danger':
          return 'error darken-1'
        case 'warning':
          return 'warning darken-1'
        case 'success':
          return 'success darken-1'
        default:
          return 'primary'
      }
    },
    bannerChanged (event, banner) {
      if (event === false) {
        this.dismissBanner(banner)
      }
    },
    /**
     * Banner content arrives as an html string (via v-html), so links inside
     * it bypass vue-router and would trigger a full page load. Intercept
     * plain clicks on relative links and push them through the router so
     * navigation stays inside the SPA. Modified clicks (new tab/window),
     * explicit link targets, absolute/external and javascript: hrefs are
     * intentionally left untouched.
     *
     * Dismisses the banner on any successful navigation, including the case
     * where the user is already on the linked page (NavigationDuplicated).
     *
     * @param {Event} event the DOM click event
     * @param {Object} banner the banner object from the v-for loop
     */
    contentClicked (event, banner) {
      const anchor = event.target.closest('a')
      if (!anchor || !event.currentTarget.contains(anchor)) {
        return
      }

      // Honor the user's intent to open the link elsewhere, the same way
      // vue-router's own RouterLink guard does.
      if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
        return
      }
      if (anchor.target && anchor.target !== '_self') {
        return
      }

      const href = anchor.getAttribute('href')
      if (!href || !href.startsWith('/') || href.startsWith('//')) {
        return
      }

      event.preventDefault()
      return this.$router.push(href)
        .then(() => this.dismissBanner(banner))
        .catch((error) => {
          // Already on this page — dismiss the banner so the user has feedback.
          if (error && error.name === 'NavigationDuplicated') {
            this.dismissBanner(banner)
            return
          }
          throw error
        })
    }
  },
  watch: {
    bannerLength: function (newVal, oldVal) {
      // Scroll into view if we've added a new banner
      if (newVal > oldVal) {
        this.$refs.banners.scrollIntoView(false)
      }
    }
  }
}
</script>
